"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ipBillStatusQuerySchema = exports.ipDeskChargeRegisterQuerySchema = exports.ipCaptureAadhaarResponseSchema = exports.ipCaptureAadhaarSchema = exports.AADHAAR_DEFERRED_SENTINEL = exports.ipUhidLookupResponseSchema = exports.ipUhidLookupAdmissionSchema = exports.ipRegistrationEmployeeQuerySchema = exports.ipRegistrationPayerQuerySchema = exports.ipUhidLookupQuerySchema = exports.listAdmissionsQuerySchema = exports.rejectCancellationSchema = exports.approveCancellationSchema = exports.cancelAdmissionSchema = exports.cancelDischargeAdviceSchema = exports.cancelDischargeSchema = exports.recordExitSchema = exports.completeDischargeSchema = exports.revokeClearanceSchema = exports.enterClearanceSchema = exports.dischargeOldAdmissionSchema = exports.adviseDischargeSchema = exports.setCostSheetProvisionSchema = exports.bedStatusChangeSchema = exports.transferBedSchema = exports.allocateBedSchema = exports.amendAdmissionSchema = exports.admitPatientSchema = exports.ipIsolationTypeEnum = exports.ipCreditCategoryEnum = exports.ipBedMovementTypeEnum = exports.ipPayerTypeEnum = exports.ipDischargeOutcomeEnum = exports.ipAdmissionStatusEnum = exports.IP_BILL_NUMBER_HINT = exports.IP_NUMBER_HINT = exports.IP_BILL_NUMBER_LABEL = exports.IP_NUMBER_LABEL = void 0;
exports.isDeferredAadhaar = isDeferredAadhaar;
const zod_1 = require("zod");
/**
 * IP Admission (ADT) contracts — ADR-085 Phase 0.
 *
 * `admissionNumber` is server-generated by the Sequence Engine and is
 * deliberately absent from every input shape, as is `status`: lifecycle is
 * moved only by the named transition endpoints (ADR-057), never by a PATCH.
 * Allowing `status` in an update body is exactly how a state machine becomes
 * decorative.
 */
/**
 * ── THE TWO NUMBERS, NAMED ONCE (desk, 2026-08-29, restated 2026-09-05) ──────
 *
 *   "IP Number issued only once per OP number but Bill number will be issued
 *    per admission of OP."
 *   "IP No is unique per OP Number… bill number will generate per admission."
 *
 *   `admissionNumber` → IP NUMBER   one per PATIENT (per OP number / UHID),
 *                                    reused on every re-admission. Live shape:
 *                                    `000053`.
 *   `billNumber`      → BILL NUMBER one per ADMISSION, and the only per-stay
 *                                    unique value on the row. Live shape:
 *                                    `IPB2026000069`.
 *
 * WHY THEY ARE CONSTANTS AND NOT STRINGS TYPED PER SCREEN. Between 2026-08-26
 * and 2026-09-05 the IP Registration and IP Lookup screens showed the pair
 * under each other's names — a caption swap from an earlier instruction that
 * the 2026-08-29 rule reversed and nobody unwound. IP Billing, the Desk
 * register, the IP reports and the advance receipts already in patients' hands
 * said the opposite, so the same stay read as two different numbers depending
 * which screen the clerk was standing at. Naming them in ONE place, shared by
 * browser and server, is what stops a caption drifting from the column again.
 *
 * The column names are NOT renamed: `admission_number` is written on every row
 * of a live table and quoted in migrations, reports and printed documents.
 */
exports.IP_NUMBER_LABEL = 'IP Number';
exports.IP_BILL_NUMBER_LABEL = 'Bill Number';
/** What each number ANSWERS — for a title, a hint or a tooltip. */
exports.IP_NUMBER_HINT = 'One per patient — the same on every admission of this OP number';
exports.IP_BILL_NUMBER_HINT = 'One per admission — new on every stay';
exports.ipAdmissionStatusEnum = zod_1.z.enum([
    'ADMITTED',
    'DISCHARGE_ADVISED',
    'PENDING_CLEARANCE',
    'BILL_FINALISED',
    'DISCHARGED',
    'EXITED',
    'CANCELLED',
    'CANCELLATION_REQUESTED',
]);
exports.ipDischargeOutcomeEnum = zod_1.z.enum([
    'DISCHARGED',
    'DAMA',
    'TRANSFERRED',
    'DECEASED',
]);
exports.ipPayerTypeEnum = zod_1.z.enum([
    'SELF_PAY',
    'INSURANCE',
    'TPA',
    'CORPORATE',
    'GOVERNMENT_SCHEME',
]);
exports.ipBedMovementTypeEnum = zod_1.z.enum([
    'ADMISSION',
    'BED_TRANSFER',
    'ROOM_TRANSFER',
    'WARD_TRANSFER',
    'DEPARTMENT_TRANSFER',
    'DISCHARGE',
]);
/**
 * WHICH KIND of credit an admission is on. A separate axis from
 * `ipPayerTypeEnum`, which answers "who is billed" — EMPLOYEE credit has no
 * payer counterparty at all, so it could not be expressed there.
 */
exports.ipCreditCategoryEnum = zod_1.z.enum(['COMPANY', 'INSURANCE', 'EMPLOYEE']);
exports.ipIsolationTypeEnum = zod_1.z.enum([
    'NONE',
    'AIRBORNE',
    'DROPLET',
    'CONTACT',
    'PROTECTIVE',
    'NEGATIVE_PRESSURE',
    'POSITIVE_PRESSURE',
]);
// ---------------------------------------------------------------------------
// Admit
// ---------------------------------------------------------------------------
exports.admitPatientSchema = zod_1.z
    .object({
    patientId: zod_1.z.string().uuid(),
    branchId: zod_1.z.string().uuid(),
    hospitalId: zod_1.z.string().uuid().optional(),
    /**
     * The bed to place the patient in. Optional so an emergency admission can
     * be recorded before a bed is found — but an admission with no occupancy
     * accrues no room rent, which the bed-board exception report surfaces.
     */
    bedId: zod_1.z.string().uuid().optional(),
    admissionTypeId: zod_1.z.string().uuid().optional(),
    admissionSourceId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    /**
     * "Admitted By" doctor. References `User`, not `Doctor` — join the master
     * via `Doctor.userId`.
     */
    consultantId: zod_1.z.string().uuid().optional(),
    /**
     * The doctor rounding on the ward, as distinct from the admitting
     * consultant. Also a `User` id.
     */
    wardDoctorId: zod_1.z.string().uuid().optional(),
    referringDoctorId: zod_1.z.string().uuid().optional(),
    /**
     * The referring doctor when they are not on staff. Mutually exclusive with
     * `referringDoctorId` — the database carries the same CHECK, because a row
     * naming both is ambiguous about which to credit.
     */
    referringDoctorName: zod_1.z.string().max(200).optional(),
    /**
     * The ward the patient is admitted into. Independent of `bedId` so an
     * emergency admission with no bed yet still records a ward; when a bed IS
     * given, the service rejects a bed that belongs to a different ward rather
     * than letting the two disagree.
     */
    wardId: zod_1.z.string().uuid().optional(),
    payerType: exports.ipPayerTypeEnum.default('SELF_PAY'),
    payerId: zod_1.z.string().uuid().optional(),
    patientCoverageId: zod_1.z.string().uuid().optional(),
    isCredit: zod_1.z.boolean().default(false),
    /** Required when `isCredit`; forbidden otherwise. */
    creditCategory: exports.ipCreditCategoryEnum.optional(),
    /** EMPLOYEE credit — the `Employee` the entitlement belongs to. */
    employeeId: zod_1.z.string().uuid().optional(),
    /** COMPANY credit — the patient's employee code at that company. */
    corporateEmployeeCode: zod_1.z.string().max(50).optional(),
    /**
     * Aadhaar, captured HERE only when the patient record does not already
     * carry one. The service writes it as a normal `IdentityDocument` through
     * the same encrypt/hash/mask path registration uses — this endpoint does
     * not get its own storage format for a national ID — and refuses to
     * overwrite an existing one.
     *
     * `999999999999` is the "patient has no Aadhaar with them" placeholder: it
     * satisfies this field so the admission can proceed, and the service
     * stores NOTHING for it (twelve nines is not a valid Aadhaar). The patient
     * stays flagged as having none, and the real number is captured later
     * through `POST /ip-registration/patients/{patientId}/aadhaar`.
     */
    aadhaarNumber: zod_1.z.string().regex(/^\d{12}$/, 'Aadhaar must be exactly 12 digits').optional(),
    isMlc: zod_1.z.boolean().default(false),
    mlcNumber: zod_1.z.string().max(50).optional(),
    policeStationId: zod_1.z.string().uuid().optional(),
    mlcReportedAt: zod_1.z.coerce.date().optional(),
    provisionalDiagnosisId: zod_1.z.string().uuid().optional(),
    dietTypeId: zod_1.z.string().uuid().optional(),
    isolationType: exports.ipIsolationTypeEnum.default('NONE'),
    expectedStayDays: zod_1.z.number().int().positive().max(365).optional(),
    expectedDischargeAt: zod_1.z.coerce.date().optional(),
    previousAdmissionId: zod_1.z.string().uuid().optional(),
    transferredFromFacility: zod_1.z.string().max(200).optional(),
    /**
     * "Cost Sheet Provision required?" (desk, 2026-09-27) — ticking this AT
     * ADMISSION approves the gate immediately, the same effect as later
     * approving it from the Cost Sheet Provision tab by bill number. Left
     * unticked, the stay starts un-approved and IP Drug Entries will not offer
     * its bill number until someone approves it from that tab.
     */
    costSheetProvisionRequired: zod_1.z.boolean().default(false),
    admittedAt: zod_1.z.coerce.date().optional(),
})
    /**
     * An MLC admission without an MLC number is the single most common
     * medico-legal data-quality failure in Indian hospital systems, and it is
     * unrecoverable after the fact. Enforced here rather than in the service so
     * it is part of the published contract.
     */
    .refine((v) => !v.isMlc || (v.mlcNumber != null && v.mlcNumber.trim().length > 0), {
    message: 'mlcNumber is required when isMlc is true',
    path: ['mlcNumber'],
})
    /**
     * A credit admission must say WHICH KIND of credit, and a self-pay one must
     * not. The database carries the same CHECK.
     */
    .refine((v) => v.isCredit === (v.creditCategory != null), {
    message: 'creditCategory is required when isCredit is true, and must be absent otherwise',
    path: ['creditCategory'],
})
    /**
     * A credit admission must name who is extending the credit.
     *
     * Widened from "payerId is always required" once EMPLOYEE credit existed:
     * staff entitlement is extended by the hospital against its own Employee
     * master, so there is no `PayerMaster` row to name and the old rule would
     * have made the category unusable. COMPANY and INSURANCE are unchanged —
     * they still must name a payer.
     */
    .refine((v) => !v.isCredit || v.creditCategory === 'EMPLOYEE' || v.payerId != null, {
    message: 'payerId is required for COMPANY and INSURANCE credit',
    path: ['payerId'],
})
    .refine((v) => v.creditCategory !== 'EMPLOYEE' || v.employeeId != null, {
    message: 'employeeId is required for EMPLOYEE credit',
    path: ['employeeId'],
})
    /**
     * The company's own reconciliation key. Without it a corporate invoice
     * cannot be matched to a beneficiary by the payer's HR, which is the single
     * most common cause of a rejected corporate claim.
     */
    .refine((v) => v.creditCategory !== 'COMPANY' ||
    (v.corporateEmployeeCode != null && v.corporateEmployeeCode.trim().length > 0), {
    message: 'corporateEmployeeCode is required for COMPANY credit',
    path: ['corporateEmployeeCode'],
})
    /** INSURANCE credit is billed against a policy, which is a `PatientCoverage`. */
    .refine((v) => v.creditCategory !== 'INSURANCE' || v.patientCoverageId != null, {
    message: 'patientCoverageId (the policy) is required for INSURANCE credit',
    path: ['patientCoverageId'],
})
    /** On-staff XOR external referrer — the database carries the same CHECK. */
    .refine((v) => v.referringDoctorId == null || v.referringDoctorName == null, {
    message: 'Give either an on-staff referring doctor or an outside doctor name, not both',
    path: ['referringDoctorName'],
});
// ---------------------------------------------------------------------------
// Amend — lifecycle fields are absent BY DESIGN
// ---------------------------------------------------------------------------
exports.amendAdmissionSchema = zod_1.z
    .object({
    consultantId: zod_1.z.string().uuid().nullish(),
    referringDoctorId: zod_1.z.string().uuid().nullish(),
    departmentId: zod_1.z.string().uuid().nullish(),
    payerType: exports.ipPayerTypeEnum.optional(),
    payerId: zod_1.z.string().uuid().nullish(),
    patientCoverageId: zod_1.z.string().uuid().nullish(),
    isCredit: zod_1.z.boolean().optional(),
    isMlc: zod_1.z.boolean().optional(),
    mlcNumber: zod_1.z.string().max(50).nullish(),
    policeStationId: zod_1.z.string().uuid().nullish(),
    mlcReportedAt: zod_1.z.coerce.date().nullish(),
    provisionalDiagnosisId: zod_1.z.string().uuid().nullish(),
    primaryDiagnosisId: zod_1.z.string().uuid().nullish(),
    dietTypeId: zod_1.z.string().uuid().nullish(),
    isolationType: exports.ipIsolationTypeEnum.optional(),
    expectedStayDays: zod_1.z.number().int().positive().max(365).nullish(),
    expectedDischargeAt: zod_1.z.coerce.date().nullish(),
    /** Required. Concurrency is real here — see ADR-085 §9. */
    lockVersion: zod_1.z.number().int().positive(),
})
    .refine((v) => v.isMlc !== true || (v.mlcNumber != null && v.mlcNumber.trim().length > 0), {
    message: 'mlcNumber is required when isMlc is true',
    path: ['mlcNumber'],
});
// ---------------------------------------------------------------------------
// Bed operations
// ---------------------------------------------------------------------------
exports.allocateBedSchema = zod_1.z.object({
    bedId: zod_1.z.string().uuid(),
    occupiedFrom: zod_1.z.coerce.date().optional(),
    remarks: zod_1.z.string().max(500).optional(),
});
exports.transferBedSchema = zod_1.z.object({
    toBedId: zod_1.z.string().uuid(),
    movementType: exports.ipBedMovementTypeEnum.default('BED_TRANSFER'),
    movedAt: zod_1.z.coerce.date().optional(),
    reasonCodeId: zod_1.z.string().uuid().optional(),
    remarks: zod_1.z.string().max(500).optional(),
    /** Guards against a transfer racing a discharge on the same admission. */
    lockVersion: zod_1.z.number().int().positive(),
});
exports.bedStatusChangeSchema = zod_1.z.object({
    toStatus: zod_1.z.enum(['AVAILABLE', 'BLOCKED', 'MAINTENANCE', 'UNDER_CLEANING']),
    /** Mandatory. A status change with no reason is an unauditable one. */
    reason: zod_1.z.string().min(1).max(500),
});
// ---------------------------------------------------------------------------
// Cost Sheet Provision — a plain approve/disable gate, NOT a lifecycle
// transition (ADR-057's numbered states are untouched by this). Toggled from
// the Cost Sheet Provision tab, by bill number, at any time in either
// direction — see `IpAdmission.costSheetProvisionApproved`'s own comment.
// ---------------------------------------------------------------------------
exports.setCostSheetProvisionSchema = zod_1.z.object({
    approved: zod_1.z.boolean(),
    /** Guards against this racing another edit on the same admission. */
    lockVersion: zod_1.z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Lifecycle transitions — one endpoint per transition (ADR-057)
// ---------------------------------------------------------------------------
exports.adviseDischargeSchema = zod_1.z.object({
    dischargeAdvisedAt: zod_1.z.coerce.date().optional(),
    expectedDischargeAt: zod_1.z.coerce.date().optional(),
    /**
     * The date/time picked in the Advise Discharge dialog (2026-09-21 redesign).
     * Stored as `dischargeScheduledAt` and becomes the admission's authoritative
     * `dischargedAt` once the bill auto-finalises; no second date prompt is asked
     * later. Validated server-side against `admittedAt` the same way
     * `completeDischargeSchema`'s date is.
     *
     * OPTIONAL SINCE 2026-10-08 — desk: "when click advise for discharge it means
     * that all entry blocks... even date and time not entered". It was required, so
     * a click with no date was REFUSED and the stay stayed open for entries: the
     * block was hostage to a form field. The block is the advice itself (status +
     * `ledgerFrozenAt`, set in the same update); the date is information. Left out,
     * `dischargeScheduledAt` stays null — "not entered" is stored as exactly that —
     * and the stay takes the time it is actually discharged at when the bill
     * finalises (`IpDischargeAutoFinaliseService` has always done
     * `dischargeScheduledAt ?? now`).
     */
    dischargedAt: zod_1.z.coerce.date().optional(),
    remarks: zod_1.z.string().max(1000).optional(),
    lockVersion: zod_1.z.number().int().positive(),
});
/**
 * DISCHARGE OLD ADMISSION — advise the discharge of a patient's running stay FROM THE REGISTRATION
 * SCREEN, so the patient can be admitted again (desk, 2026-10-09).
 *
 * It is the same advice as `adviseDischargeSchema` (status DISCHARGE_ADVISED, ledger frozen in the
 * same update) with one difference that matters: the REASON is mandatory. The front desk takes
 * this decision, billing inherits the unpaid balance, and the "Unsettled IP" tab in IP Billing shows
 * who did it, when and why — a record that is only worth having if every row has a reason.
 * `reason` is trimmed before the length check, like `cancelDischargeAdviceSchema`'s.
 *
 * `dischargedAt` is optional for the reason `adviseDischargeSchema` documents: the click is what
 * blocks the bill, the date is information.
 */
exports.dischargeOldAdmissionSchema = zod_1.z.object({
    reason: zod_1.z
        .string()
        .trim()
        .min(5, 'A reason of at least 5 characters is required.')
        .max(1000),
    dischargedAt: zod_1.z.coerce.date().optional(),
    lockVersion: zod_1.z.number().int().positive(),
});
/**
 * DISCHARGE_ADVISED -> PENDING_CLEARANCE.
 *
 * The step the ward has always taken and the system never recorded: the
 * clinician has said the patient may go, and the stay now waits on the desks
 * that have to clear it — pharmacy returns, investigations, the bill. Nothing
 * about the money moves here; this is the state in which a stay is chased.
 */
exports.enterClearanceSchema = zod_1.z.object({
    remarks: zod_1.z.string().max(1000).optional(),
    lockVersion: zod_1.z.number().int().positive(),
});
/**
 * PENDING_CLEARANCE -> ADMITTED, the ONE backward edge in the lifecycle.
 *
 * A patient who deteriorates while waiting to be cleared is back in care, and
 * the stay must be able to say so — otherwise the ward's only options are to
 * discharge someone who is not going home or to leave the record lying.
 *
 * THE REASON IS MANDATORY. A backward transition with no explanation is
 * unauditable, and this is the only edge that can un-freeze a stay the
 * clearance desks were already working on.
 */
exports.revokeClearanceSchema = zod_1.z.object({
    reason: zod_1.z.string().min(1).max(1000),
    lockVersion: zod_1.z.number().int().positive(),
});
exports.completeDischargeSchema = zod_1.z.object({
    dischargeOutcome: exports.ipDischargeOutcomeEnum,
    dischargedAt: zod_1.z.coerce.date().optional(),
    remarks: zod_1.z.string().max(1000).optional(),
    lockVersion: zod_1.z.number().int().positive(),
});
exports.recordExitSchema = zod_1.z.object({
    exitedAt: zod_1.z.coerce.date().optional(),
    remarks: zod_1.z.string().max(1000).optional(),
    lockVersion: zod_1.z.number().int().positive(),
});
/**
 * DISCHARGED -> ADMITTED, inside the 48-hour window
 * (`IP_CANCEL_DISCHARGE_WINDOW_HOURS`). Reopens the stay for further entries and
 * changes NOTHING already posted — see `IpAdmissionApplicationService
 * .cancelDischarge`.
 *
 * `reason` is MANDATORY and trimmed before the length check, so whitespace cannot
 * satisfy it. `lockVersion` is the same optimistic lock every other lifecycle
 * move carries: two desks cancelling one discharge cannot both win.
 */
exports.cancelDischargeSchema = zod_1.z.object({
    reason: zod_1.z
        .string()
        .trim()
        .min(5, 'A cancellation reason of at least 5 characters is required.')
        .max(1000),
    lockVersion: zod_1.z.number().int().positive(),
});
/**
 * DISCHARGE_ADVISED -> ADMITTED. Withdraws a discharge ADVICE that has not yet
 * become a bill, so the stay is open for entries again — see
 * `IpAdmissionApplicationService.cancelDischargeAdvice`. No time limit: nothing
 * has been billed or posted at this stage, so there is nothing for a window to
 * protect (contrast `cancelDischargeSchema`, which reopens a CLOSED bill).
 *
 * `reason` is MANDATORY and trimmed before the length check, exactly like
 * `cancelDischargeSchema`; `lockVersion` is the same optimistic lock.
 */
exports.cancelDischargeAdviceSchema = zod_1.z.object({
    reason: zod_1.z
        .string()
        .trim()
        .min(5, 'A reason of at least 5 characters is required.')
        .max(1000),
    lockVersion: zod_1.z.number().int().positive(),
});
exports.cancelAdmissionSchema = zod_1.z.object({
    /** Mandatory — a cancelled admission with no reason cannot be audited. */
    cancelReason: zod_1.z.string().min(1).max(1000),
    lockVersion: zod_1.z.number().int().positive(),
});
/**
 * A cancellation request approved — desk, 2026-09-25. No reason field: the
 * one already on the request (`cancelAdmissionSchema.cancelReason`) is what
 * is being acted on, and approving is not itself a second, separate reason.
 */
exports.approveCancellationSchema = zod_1.z.object({
    lockVersion: zod_1.z.number().int().positive(),
});
/** A cancellation request sent back to ADMITTED. The reason is optional — an approver may simply disagree. */
exports.rejectCancellationSchema = zod_1.z.object({
    reason: zod_1.z.string().max(1000).optional(),
    lockVersion: zod_1.z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
exports.listAdmissionsQuerySchema = zod_1.z.object({
    search: zod_1.z.string().max(120).optional(),
    /**
     * WHICH FIELDS `search` IS ALLOWED TO MATCH.
     *
     * `ALL` (the default, and every existing caller) matches the IP number, the
     * bill number, the OP Number, both name parts, a contact number and the bed
     * code — which is right for the IP register, where a clerk holds any of them.
     *
     * `DOCUMENTS` matches only the BILL NUMBER, the OP NUMBER and the NAME.
     *
     * ── WHY THE NARROW MODE EXISTS ────────────────────────────────────────────
     * Desk, 2026-09-09, on the Lab/Radiology billing screen: "why if type 83 arya
     * is showing here?" Typing `83` returned AYRA, whose row displayed bill
     * number `000085` and OP Number `00051G` — neither contains `83`. It matched
     * her IP NUMBER, `000083`, which that screen deliberately no longer shows
     * (desk, minutes earlier: "here IP Number should show only Bill number, OP
     * Number or name").
     *
     * A SEARCH THAT MATCHES A FIELD THE ROW DOES NOT SHOW IS UNREADABLE. The
     * operator sees a result they cannot account for and stops trusting the list.
     * `BILL_NUMBER` matches ONLY the bill number.
     *
     * The IP Billing census asks for exactly one thing — its field is labelled
     * BILL NUMBER and the desk's own instruction was "user will type only bill
     * number". Typing `78` there returned stays numbered 000089 and 000080, which
     * contain no `78` anywhere the operator could see: they matched a mobile
     * number. A search that answers with rows the typed string does not appear in
     * is one the operator stops trusting, however correct it is.
     *
     * So the billing desk asks for the fields it prints, and the IP register —
     * which does print the IP number — keeps matching everything.
     *
     * `ADMISSION_NUMBER` matches ONLY the IP number — desk, 2026-09-12: the
     * Enquire tab's whole premise is "billing staff enter a PARTICULAR IP
     * NUMBER", which `ALL` cannot serve on its own: it also matches the bill
     * number, name, mobile and bed code, so a query meant to be exact could
     * answer with an unrelated patient the same way `83` did on the Lab desk
     * (the incident `DOCUMENTS` and `BILL_NUMBER` above were both cut to fix).
     * The SAME `admissionNumber` is per-PATIENT and reused on re-admission, so
     * this can legitimately answer with more than one row — the caller shows
     * them and lets the operator pick, exactly as `BILL_NUMBER` already does.
     */
    searchScope: zod_1.z.enum(['ALL', 'DOCUMENTS', 'BILL_NUMBER', 'ADMISSION_NUMBER']).default('ALL'),
    status: exports.ipAdmissionStatusEnum.optional(),
    wardId: zod_1.z.string().uuid().optional(),
    roomId: zod_1.z.string().uuid().optional(),
    bedId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    consultantId: zod_1.z.string().uuid().optional(),
    payerId: zod_1.z.string().uuid().optional(),
    payerType: exports.ipPayerTypeEnum.optional(),
    admittedFrom: zod_1.z.coerce.date().optional(),
    admittedTo: zod_1.z.coerce.date().optional(),
    /**
     * IP DRUG ENTRIES' OWN PICKER (desk, 2026-09-27) — `true` narrows to stays
     * whose Cost Sheet Provision is currently approved; that screen is the one
     * caller that ever sends this. Absent everywhere else, exactly like every
     * other optional filter here.
     */
    costSheetProvisionApproved: zod_1.z.coerce.boolean().optional(),
    /**
     * NEW OR REPEAT PATIENT — the IP Admission Register's "New patient / Repeat
     * patient" control (desk, 2026-10-07: "need New and repeat patient sort button
     * as per appointment register").
     *
     * `NEW` is a stay that IS the patient's earliest non-cancelled admission;
     * `REPEAT` is every other stay. That rule is the analytics classifier's own
     * (`ip-admission-classifier.ts`) — the register imports it rather than keeping a
     * second definition, so this filter and the IP reports can never disagree
     * about the same patient. DELIBERATELY NOT `previousAdmissionId`, which is
     * NULL on the imported history.
     *
     * Absent for every other caller, which sees every stay exactly as before.
     */
    visitType: zod_1.z.enum(['NEW', 'REPEAT']).optional(),
    /** Server pagination is mandatory — the workstation must hold 1,000+ admissions. */
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(100).default(25),
    /**
     * EVERY COLUMN THE REGISTER PRINTS THAT HAS ONE MEANINGFUL ORDER, and
     * nothing else. `mobile` and `createdByName` are deliberately absent:
     * the first is a per-patient array with no single sortable value, the
     * second is resolved by a separate batched lookup rather than a Prisma
     * relation the repository can order by — see `ip-admission.repository.ts`.
     */
    sortBy: zod_1.z
        .enum([
        'admittedAt',
        'admissionNumber',
        'billNumber',
        'patientName',
        'dischargedAt',
        'status',
        'payerType',
        'departmentName',
        'wardName',
        'consultantName',
    ])
        .default('admittedAt'),
    sortDir: zod_1.z.enum(['asc', 'desc']).default('desc'),
});
// ---------------------------------------------------------------------------
// UHID lookup — the registration desk's "who is this, and are they already in?"
// ---------------------------------------------------------------------------
/**
 * Takes the UHID as the operator typed it. NOT a uuid: the clerk is holding a
 * card, and requiring them to know a patient's internal id would make the
 * panel unusable by the only people who need it.
 */
exports.ipUhidLookupQuerySchema = zod_1.z.object({
    uhid: zod_1.z.string().min(1).max(40),
});
/** Narrows the payer picker to the credit category the desk chose. */
exports.ipRegistrationPayerQuerySchema = zod_1.z.object({
    category: zod_1.z.enum(['COMPANY', 'INSURANCE']),
});
exports.ipRegistrationEmployeeQuerySchema = zod_1.z.object({
    search: zod_1.z.string().max(120).optional(),
});
/**
 * One prior stay in the lookup panel's history list.
 *
 * `billNumber` is nullable: admissions created before the IP Registration Desk
 * existed have none, and showing a blank cell is honest where inventing a
 * number would not be.
 */
exports.ipUhidLookupAdmissionSchema = zod_1.z.object({
    admissionId: zod_1.z.string().uuid(),
    admissionNumber: zod_1.z.string(),
    billNumber: zod_1.z.string().nullable(),
    status: exports.ipAdmissionStatusEnum,
    admittedAt: zod_1.z.string(),
    dischargedAt: zod_1.z.string().nullable(),
    wardName: zod_1.z.string().nullable(),
    consultantName: zod_1.z.string().nullable(),
    /**
     * The admitting department. Added for the desk's "already admitted" state:
     * when a running stay blocks a new admission, the three fields the clerk was
     * about to fill (department, ward, admitted by) show what the CURRENT stay
     * already holds, and department was the one of the three this row could not
     * answer.
     */
    departmentName: zod_1.z.string().nullable(),
    /** True for the ONE stay that is currently occupying — see `hasRunningAdmission`. */
    isRunning: zod_1.z.boolean(),
});
exports.ipUhidLookupResponseSchema = zod_1.z.object({
    patient: zod_1.z.object({
        id: zod_1.z.string().uuid(),
        uhid: zod_1.z.string(),
        fullName: zod_1.z.string(),
        gender: zod_1.z.string().nullable(),
        dateOfBirth: zod_1.z.string().nullable(),
        age: zod_1.z.number().int().nullable(),
        mobile: zod_1.z.string().nullable(),
        /**
         * Drives the conditional Aadhaar field. A BOOLEAN, never the number: the
         * desk needs to know whether to ASK, and shipping a national ID to a
         * browser that only needed a yes/no is how a decrypted Aadhaar ends up in
         * a screenshot. The masked form is enough to show it is on file.
         */
        hasAadhaar: zod_1.z.boolean(),
        aadhaarMasked: zod_1.z.string().nullable(),
    }),
    /**
     * Whether the patient is CURRENTLY admitted. The desk's real question — it
     * decides whether this is a new admission or a duplicate about to be
     * rejected by the one-open-admission rule.
     */
    hasRunningAdmission: zod_1.z.boolean(),
    runningAdmission: exports.ipUhidLookupAdmissionSchema.nullable(),
    /** Newest first, running stay included. */
    admissions: zod_1.z.array(exports.ipUhidLookupAdmissionSchema),
});
/**
 * "The patient has no Aadhaar with them" — typed into the admission form's
 * Aadhaar field so the stay can be opened without one.
 *
 * Lives in the CONTRACT, not in the screen and not in the service, because the
 * browser that offers it and the server that must refuse to store it have to
 * agree on the exact value; two copies of a sentinel is one copy too many.
 * Twelve nines is not a valid Aadhaar (a real one never starts with 0 or 1 and
 * carries a Verhoeff check digit, which this fails), so it cannot collide with
 * a genuine number somebody means to keep.
 */
exports.AADHAAR_DEFERRED_SENTINEL = '999999999999';
/** True for the deferred sentinel — all twelve digits nine. */
function isDeferredAadhaar(value) {
    return /^9{12}$/.test(value.trim());
}
/**
 * Deferred Aadhaar capture — the number a patient could not produce at the
 * desk, saved afterwards.
 *
 * Deliberately REFUSES the all-nines "not available" placeholder the admit
 * form accepts (`999999999999`): admission may proceed without an Aadhaar, but
 * the endpoint whose entire purpose is to record the real number must not
 * accept the stand-in and report success. The server enforces it too — this
 * regex only spares the operator a round trip.
 */
exports.ipCaptureAadhaarSchema = zod_1.z.object({
    aadhaarNumber: zod_1.z
        .string()
        .regex(/^\d{12}$/, 'Aadhaar must be exactly 12 digits')
        .refine((value) => !isDeferredAadhaar(value), {
        message: '999999999999 is the "not available" placeholder — enter the number printed on the card.',
    }),
});
/**
 * What the capture did. `UNCHANGED` is not an error: a clerk re-confirming the
 * number already on file should not be told they broke something.
 */
exports.ipCaptureAadhaarResponseSchema = zod_1.z.object({
    status: zod_1.z.enum(['CAPTURED', 'REPLACED', 'UNCHANGED']),
    /** Masked only — see `hasAadhaar` above; no plaintext national ID reaches a browser. */
    maskedValue: zod_1.z.string(),
});
/**
 * WHAT ONE DESK BILLED ONTO ADMISSIONS — the IP half of the Laboratory and
 * Radiology registers (desk, 2026-08-29: "need lab and radiology screen
 * register of what they are billed including IP and OP bills").
 *
 * `sourceType` is REQUIRED and has no default. This register answers "what did
 * THIS counter bill", and a defaulted source would quietly answer a different
 * question — every desk's charges under one desk's heading — which is worse
 * than refusing.
 *
 * Dates are wall-clock `YYYY-MM-DD`; the controller closes the window at the
 * END of `to`, so "today to today" covers the day it names.
 */
exports.ipDeskChargeRegisterQuerySchema = zod_1.z.object({
    sourceType: zod_1.z.enum(['LAB', 'RADIOLOGY', 'PHARMACY', 'PROCEDURE', 'OT']),
    from: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(500).optional(),
});
/**
 * IP BILL STATUS — every IP bill number with the lines standing behind it, both
 * halves (desk, 2026-09-30: "under cash counter, need IP Bill Status sub tab…
 * show all saved, approved lines of ward, lab, drugs whatever is charges, those
 * bill numbers show detailed and condensed view").
 *
 * ── WHY ONE READ COVERS TWO TABLES ─────────────────────────────────────────
 * A line is SAVED while it sits in `ip_charge_drafts` and APPROVED the moment
 * the counter ticks it, which MOVES it into `ip_admission_charge_ledger`. A
 * register fed by only one of them is the footer defect the desk has reported
 * three times: a bill with 17 saved lines against 2 approved ones reads as
 * nearly nothing. So both halves are returned, and every figure is summed on
 * the server — the screen renders, it adds nothing up.
 *
 * `search` matches ONLY what the screen prints — bill number, IP number, UHID
 * and name — never a mobile number or a bed code. See `searchScope` on
 * `listAdmissionsQuerySchema` for the incident that rule comes from.
 *
 * Dates are wall-clock `YYYY-MM-DD` on the ADMISSION date, closed at the end of
 * `to`. They are optional: a bill number typed into the search must be found
 * whenever the patient was admitted.
 */
exports.ipBillStatusQuerySchema = zod_1.z.object({
    search: zod_1.z.string().trim().max(120).optional(),
    /** `IN_HOUSE` = charging still open; `DISCHARGED` = discharged or exited. */
    stay: zod_1.z.enum(['ALL', 'IN_HOUSE', 'DISCHARGED']).default('ALL'),
    /** `HAS_SAVED` = at least one line still awaiting approval. */
    lines: zod_1.z.enum(['ALL', 'HAS_SAVED', 'ALL_APPROVED']).default('ALL'),
    from: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(100).default(25),
});
//# sourceMappingURL=ip-admission.schema.js.map